import { create } from 'zustand';

// How the live camera feed is laid out inside its box on the exercise page.
//   default — the shipped look: the video fills its box and whatever does not fit
//             is cropped away (object-cover). Every existing exercise assumed it.
//   contain — the same box, but nothing is cropped: the frame is letterboxed.
//             This is what dropping `object-cover` gives you, since the UA sheet
//             already sets `object-fit: contain` on <video>.
//   fill    — the same box, nothing cropped and no bars either: the frame is
//             stretched onto it (object-fit: fill), so the aspect ratio is not
//             preserved.
//
// The skeleton overlay is projected through the matching fit (see
// getCameraViewObjectFit) — a mode that changes the display without changing that
// projection puts the joints off the body by exactly the crop/letterbox amount.
export const CAMERA_VIEW_MODES = Object.freeze({
  default: 'default',
  contain: 'contain',
  fill: 'fill',
});

// No UI exposes this choice (the pause modal's "Camera view" section was
// removed) — the only thing deciding it is the device: desktop gets the
// letterboxed "fit without cropping" look, mobile gets the cropped cover fill
// (fills the box, reads better on a small screen than letterboxed bars).
// Matches the `lg` breakpoint (1024px, see tailwind.config.js) the rest of the
// exercise page already treats as the desktop/mobile split.
//
// Deliberately not persisted: with no UI to set it, a value stored from
// before that section was removed would otherwise stick forever and silently
// override this device default (e.g. a phone stuck on the desktop's
// letterboxed look).
const getDeviceDefaultMode = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return CAMERA_VIEW_MODES.default;
  }

  return window.matchMedia('(min-width: 1024px)').matches
    ? CAMERA_VIEW_MODES.contain
    : CAMERA_VIEW_MODES.default;
};

export const useCameraViewMode = create(() => ({
  mode: getDeviceDefaultMode(),
}));

/**
 * The object-fit the skeleton overlay has to be projected through to stay on the
 * body — the mode names map 1:1 onto the CSS values, `default` being `cover`.
 * Read through `getState()` because the only caller is the MediaPipe frame loop,
 * where a hook subscription — and the re-render it brings — is not an option.
 */
export function getCameraViewObjectFit() {
  const { mode } = useCameraViewMode.getState();
  return mode === CAMERA_VIEW_MODES.default ? 'cover' : mode;
}
